/**
 * Memory API Client
 *
 * Client for the centralized AI memory API.
 * Used by both marta-admin and the main app to access the same data.
 */

import type {
  AIBaseInstruction,
  AISector,
  AISectorInstruction,
  AICollectivePattern,
  AIUserPreference,
  AICompiledContext,
  MemoryStats,
} from './types';

import { getApiEndpoints } from '../../config';

// API base URL - routed through backend gateway
const getApiBase = () => getApiEndpoints().memory;

// Auth: user JWT token (gateway validates and routes to memory service)
function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('accessToken');
  if (token) return { Authorization: `Bearer ${token}` };
  return {};
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

async function apiCall<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${getApiBase()}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
      ...options.headers,
    },
  });

  const result: ApiResponse<T> = await response.json();

  if (!result.success) {
    throw new Error(result.error || 'API request failed');
  }

  return result.data as T;
}

// =============================================================================
// Layer 1: Base Instructions
// =============================================================================

export async function fetchBaseInstructions(): Promise<AIBaseInstruction[]> {
  return apiCall<AIBaseInstruction[]>('/base-instructions');
}

export async function saveBaseInstructionApi(
  instruction: AIBaseInstruction
): Promise<AIBaseInstruction> {
  return apiCall<AIBaseInstruction>('/base-instructions', {
    method: 'POST',
    body: JSON.stringify(instruction),
  });
}

export async function deleteBaseInstructionApi(id: string): Promise<void> {
  await apiCall(`/base-instructions/${id}`, { method: 'DELETE' });
}

// =============================================================================
// Layer 2: Sectors
// =============================================================================

export async function fetchSectors(): Promise<AISector[]> {
  return apiCall<AISector[]>('/sectors');
}

export async function saveSectorApi(sector: AISector): Promise<AISector> {
  return apiCall<AISector>('/sectors', {
    method: 'POST',
    body: JSON.stringify(sector),
  });
}

export async function deleteSectorApi(id: string): Promise<void> {
  await apiCall(`/sectors/${id}`, { method: 'DELETE' });
}

export async function fetchSectorInstructions(
  sectorId?: string
): Promise<AISectorInstruction[]> {
  const query = sectorId ? `?sectorId=${sectorId}` : '';
  return apiCall<AISectorInstruction[]>(`/sector-instructions${query}`);
}

export async function saveSectorInstructionApi(
  instruction: AISectorInstruction
): Promise<AISectorInstruction> {
  return apiCall<AISectorInstruction>('/sector-instructions', {
    method: 'POST',
    body: JSON.stringify(instruction),
  });
}

// =============================================================================
// Layer 3: Collective Patterns
// =============================================================================

export async function fetchPatterns(
  status?: 'pending' | 'approved' | 'rejected',
  sectorId?: string
): Promise<AICollectivePattern[]> {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (sectorId) params.append('sectorId', sectorId);

  const query = params.toString() ? `?${params.toString()}` : '';
  return apiCall<AICollectivePattern[]>(`/patterns${query}`);
}

export async function savePatternApi(
  pattern: AICollectivePattern
): Promise<AICollectivePattern> {
  return apiCall<AICollectivePattern>('/patterns', {
    method: 'POST',
    body: JSON.stringify(pattern),
  });
}

export async function approvePatternApi(id: string): Promise<AICollectivePattern> {
  return apiCall<AICollectivePattern>(`/patterns/${id}/approve`, {
    method: 'POST',
  });
}

export async function rejectPatternApi(id: string): Promise<AICollectivePattern> {
  return apiCall<AICollectivePattern>(`/patterns/${id}/reject`, {
    method: 'POST',
  });
}

export async function recordPatternApi(pattern: {
  patternType: string;
  patternKey: string;
  patternValue: string;
  sectorId?: string;
  confidence: number;
}): Promise<{ pattern: AICollectivePattern; isNew: boolean }> {
  const result = await apiCall<{ data: AICollectivePattern; isNew: boolean }>(
    '/patterns/record',
    {
      method: 'POST',
      body: JSON.stringify(pattern),
    }
  );
  // The API returns data and isNew at the same level
  return { pattern: result as unknown as AICollectivePattern, isNew: false };
}

// =============================================================================
// Layer 4: User Preferences
// =============================================================================

export async function fetchUserPreferences(
  userId: string
): Promise<AIUserPreference[]> {
  return apiCall<AIUserPreference[]>(`/user-preferences/${userId}`);
}

export async function saveUserPreferenceApi(
  userId: string,
  preference: Omit<AIUserPreference, 'userId'>
): Promise<AIUserPreference> {
  return apiCall<AIUserPreference>(`/user-preferences/${userId}`, {
    method: 'POST',
    body: JSON.stringify(preference),
  });
}

// =============================================================================
// Compile Context
// =============================================================================

export async function compileContextApi(
  userId: string,
  sectorIds?: string[]
): Promise<AICompiledContext> {
  const params = new URLSearchParams();
  if (sectorIds?.length) {
    sectorIds.forEach((id) => params.append('sectorIds', id));
  }

  const query = params.toString() ? `?${params.toString()}` : '';
  return apiCall<AICompiledContext>(`/compile/${userId}${query}`);
}

// =============================================================================
// Stats
// =============================================================================

export async function fetchMemoryStats(): Promise<MemoryStats> {
  return apiCall<MemoryStats>('/stats');
}

// =============================================================================
// Seed
// =============================================================================

export async function seedDefaultData(): Promise<{
  message: string;
  skipped?: boolean;
  counts?: { baseInstructions: number; sectors: number };
}> {
  return apiCall('/seed', { method: 'POST' });
}

// =============================================================================
// Feedback
// =============================================================================

export interface FeedbackData {
  rating: 'bad' | 'fine' | 'good' | 'great';
  comment?: string;
  feedbackType: 'global' | 'chat_specific';
  chatId?: string;
  messageCount?: number;
  userId?: string;
}

export interface FeedbackStats {
  total: number;
  byRating: {
    bad: number;
    fine: number;
    good: number;
    great: number;
  };
  byType: {
    global: number;
    chat_specific: number;
  };
  withComments: number;
  averageScore: number;
}

export async function submitFeedbackApi(feedback: FeedbackData): Promise<FeedbackData & { id: string; timestamp: string }> {
  return apiCall('/feedback', {
    method: 'POST',
    body: JSON.stringify(feedback),
  });
}

export async function fetchFeedbackList(): Promise<(FeedbackData & { id: string; timestamp: string })[]> {
  return apiCall('/feedback');
}

export async function fetchFeedbackStats(): Promise<FeedbackStats> {
  return apiCall('/feedback/stats');
}
